import { useEffect, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Stack,
  Badge,
  Button,
  Alert as MuiAlert,
} from '@mui/material'
import NotificationsIcon from '@mui/icons-material/Notifications'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import InfoIcon from '@mui/icons-material/Info'
import { WidgetProps, Alert } from './types'
import { systemService } from '@/services/systemService'

interface AlertCenterWidgetProps extends WidgetProps {
  maxAlerts?: number
}

const AlertCenterWidget = ({ config, maxAlerts = 10, onUpdate, onRemove }: AlertCenterWidgetProps) => {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(false)

  const fetchAlerts = async () => {
    setLoading(true)
    try {
      // Generate alerts based on system status and dashboard data
      const systemStatus = await systemService.fetchSystemStatus().catch(() => null)
      const newAlerts: Alert[] = []

      if (systemStatus) {
        if (systemStatus.database === 'down') {
          newAlerts.push({
            id: 'db-down',
            type: 'critical',
            category: 'System Health',
            title: 'Database Offline',
            message: 'Database connection is down. Immediate attention required.',
            timestamp: new Date().toISOString(),
            acknowledged: false,
          })
        }

        if (systemStatus.status === 'degraded') {
          newAlerts.push({
            id: 'system-degraded',
            type: 'warning',
            category: 'System Health',
            title: 'System Degraded',
            message: 'System is running in degraded mode. Some features may be unavailable.',
            timestamp: new Date().toISOString(),
            acknowledged: false,
          })
        }

        if (systemStatus.responseTimeMs > 1000) {
          newAlerts.push({
            id: 'slow-response',
            type: 'warning',
            category: 'Performance',
            title: 'Slow Response Time',
            message: `API response time is ${systemStatus.responseTimeMs}ms. Consider investigating.`,
            timestamp: new Date().toISOString(),
            acknowledged: false,
          })
        }
      }

      setAlerts(newAlerts.slice(0, maxAlerts))
    } catch (error) {
      console.error('Failed to fetch alerts', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAlerts()
    const interval = setInterval(fetchAlerts, 60000) // Check every minute
    return () => clearInterval(interval)
  }, [maxAlerts])

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <ErrorIcon color="error" fontSize="small" />
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />
      default:
        return <InfoIcon color="info" fontSize="small" />
    }
  }

  const getAlertColor = (type: string): 'error' | 'warning' | 'info' => {
    switch (type) {
      case 'critical':
        return 'error'
      case 'warning':
        return 'warning'
      default:
        return 'info'
    }
  }

  const handleAcknowledge = (alertId: string) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId
          ? {
              ...alert,
              acknowledged: true,
              acknowledgedAt: new Date().toISOString(),
            }
          : alert
      )
    )
  }

  const unreadCount = alerts.filter((a) => !a.acknowledged).length

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
              <Typography variant="h6">Alerts</Typography>
            </Stack>
            {unreadCount > 0 && (
              <Button size="small" onClick={() => setAlerts((prev) => prev.map((a) => ({ ...a, acknowledged: true })))}>
                Mark all read
              </Button>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            maxHeight: 400,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}
        >
          {loading && alerts.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Loading alerts...
              </Typography>
            </Box>
          ) : alerts.length === 0 ? (
            <Box sx={{ p: 2 }}>
              <MuiAlert severity="success">No active alerts. System is running smoothly.</MuiAlert>
            </Box>
          ) : (
            <List dense sx={{ p: 0 }}>
              {alerts.map((alert) => (
                <ListItem
                  key={alert.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: alert.acknowledged ? 'transparent' : 'action.hover',
                    opacity: alert.acknowledged ? 0.6 : 1,
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {getAlertIcon(alert.type)}
                        <Typography variant="subtitle2" fontWeight={alert.acknowledged ? 400 : 600}>
                          {alert.title}
                        </Typography>
                        <Chip
                          label={alert.category}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                        {!alert.acknowledged && (
                          <IconButton
                            size="small"
                            onClick={() => handleAcknowledge(alert.id)}
                            sx={{ ml: 'auto' }}
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {alert.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(alert.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default AlertCenterWidget


